import React from "react";
import { Link } from "@inertiajs/react";
import styled from "styled-components";
import {
  brand_orange_deep,
  brand_gradient,
  card_shadow_sm,
} from "../../constants/styles";

// Right-rail sibling lessons + next-lesson auto-advance toggle. Persists the
// auto-advance preference to localStorage so subsequent lesson views remember.
const STORAGE_KEY = "sertifikasi.autoAdvance";

// `title` is overridable because a standalone lesson has no module: its siblings
// are the other videos sharing its Nama Materi Belajar, so "Materi di Modul Ini"
// would be plainly wrong there.
function LessonSiblings({
  siblings = [],
  nextLesson,
  autoAdvance,
  onToggleAutoAdvance,
  title = "Materi di Modul Ini",
}) {
  if (!siblings.length && !nextLesson) return null;

  const total = siblings.length;
  const completed = siblings.filter(
    (s) => Math.max(0, Math.min(100, Number(s.progress_percent) || 0)) >= 100,
  ).length;
  const headerProgress = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <Card>
      {nextLesson && (
        <NextLessonRow>
          <NextLessonHead>
            <NextLessonLabel>Lanjut Berikutnya</NextLessonLabel>
            <AutoToggle>
              <AutoToggleLabel>Putar otomatis</AutoToggleLabel>
              <Switch
                type="button"
                role="switch"
                aria-checked={!!autoAdvance}
                aria-label="Putar materi berikutnya secara otomatis"
                onClick={() => onToggleAutoAdvance?.(!autoAdvance)}
                $on={!!autoAdvance}
              >
                <SwitchThumb $on={!!autoAdvance} />
              </Switch>
            </AutoToggle>
          </NextLessonHead>
          <NextLessonLink href={nextLesson.detail_url}>
            {nextLesson.name} →
          </NextLessonLink>
        </NextLessonRow>
      )}
      <Header>
        <HeaderTitle>{title}</HeaderTitle>
        {total > 0 && (
          <HeaderMeta>
            {completed} dari {total} selesai
          </HeaderMeta>
        )}
      </Header>
      {total > 0 && (
        <HeaderBar
          role="progressbar"
          aria-valuenow={headerProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <HeaderFill style={{ width: `${headerProgress}%` }} />
        </HeaderBar>
      )}
      {siblings.length > 0 && (
        <List>
          {siblings.map((s, idx) => {
            const pct = Math.max(
              0,
              Math.min(100, Number(s.progress_percent) || 0),
            );
            const isCompleted = pct >= 100;
            return (
              <Item key={s.id} $active={s.is_current}>
                <Bullet $completed={isCompleted}>
                  {isCompleted && !s.is_current ? "✓" : idx + 1}
                </Bullet>
                <ItemBody>
                  {s.is_current ? (
                    <CurrentLabel>{s.name}</CurrentLabel>
                  ) : (
                    <ItemLink href={s.detail_url}>{s.name}</ItemLink>
                  )}
                  <ItemMeta>
                    {s.is_current
                      ? "Anda di sini"
                      : isCompleted
                        ? "Selesai"
                        : pct > 0
                          ? `${pct}% ditonton`
                          : "Belum ditonton"}
                  </ItemMeta>
                  {!s.is_current && pct > 0 && !isCompleted && (
                    <MiniBar
                      role="progressbar"
                      aria-valuenow={pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <MiniFill style={{ width: `${pct}%` }} />
                    </MiniBar>
                  )}
                </ItemBody>
              </Item>
            );
          })}
        </List>
      )}
    </Card>
  );
}

LessonSiblings.STORAGE_KEY = STORAGE_KEY;

export default React.memo(LessonSiblings);

const Card = styled.aside`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: ${card_shadow_sm};

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: none;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${brand_orange_deep};
`;

const HeaderMeta = styled.span`
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const HeaderBar = styled.div`
  height: 4px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  overflow: hidden;
  margin-top: -4px;

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const HeaderFill = styled.div`
  height: 100%;
  background: ${brand_gradient};
  transition: width 320ms ease;
`;

const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Item = styled.li`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${({ $active }) =>
    $active ? "rgba(216,89,7,0.08)" : "transparent"};
`;

const Bullet = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $completed }) =>
    $completed ? "#10b981" : "linear-gradient(135deg, #ef8726, #d85907)"};
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  flex-shrink: 0;
`;

const MiniBar = styled.div`
  height: 3px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.06);
  overflow: hidden;
  margin-top: 4px;
  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const MiniFill = styled.div`
  height: 100%;
  background: ${brand_gradient};
  transition: width 320ms ease;
`;

const ItemBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const ItemLink = styled(Link)`
  color: #1f2937;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    color: ${brand_orange_deep};
  }
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const CurrentLabel = styled.span`
  color: ${brand_orange_deep};
  font-weight: 800;
  font-size: 13px;
`;

const ItemMeta = styled.span`
  font-size: 11px;
  color: #6b7280;
  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const NextLessonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(216, 89, 7, 0.08),
    rgba(247, 179, 12, 0.06)
  );
  border: 1px solid rgba(216, 89, 7, 0.22);

  [data-theme="dark"] & {
    background: rgba(216, 89, 7, 0.12);
    border-color: rgba(216, 89, 7, 0.32);
  }
`;

const NextLessonHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const NextLessonLabel = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${brand_orange_deep};
`;

const NextLessonLink = styled(Link)`
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
  text-decoration: none;
  line-height: 1.4;
  &:hover {
    text-decoration: underline;
    color: ${brand_orange_deep};
  }
  [data-theme="dark"] & {
    color: #f3f4f6;
  }
`;

const AutoToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
  user-select: none;

  [data-theme="dark"] & {
    color: #cbd5e1;
  }
`;

const AutoToggleLabel = styled.span`
  font-weight: 600;
`;

const Switch = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 20px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  background: ${({ $on }) =>
    $on ? brand_orange_deep : "rgba(15, 23, 42, 0.18)"};
  transition: background 150ms ease;
  padding: 0;

  &:focus-visible {
    outline: 2px solid #fdba74;
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    background: ${({ $on }) =>
      $on ? brand_orange_deep : "rgba(255, 255, 255, 0.18)"};
  }
`;

const SwitchThumb = styled.span`
  position: absolute;
  top: 2px;
  left: ${({ $on }) => ($on ? "18px" : "2px")};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
  transition: left 150ms ease;
`;
